<template>
  <div class="search-results-list">
    <Suspense>
      <div
        v-for="(result, index) in results"
        :key="resultKey(result, index)"
        ref="resultEls"
        class="result"
        :class="{ selected: index === selected }"
        @mouseover="onMouseOver(index)"
        @click="$emit('choose', selected)"
      >
        <SearchResultRow
          :result="result"
          :show-highlighted-content="showHighlightedContent"
          :context-workspace-id="contextWorkspaceId"
          :show-global-context="showGlobalContext"
        />
      </div>
    </Suspense>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType, ref, toRefs, watch } from "vue";

import type { WorkspaceId } from "@ctrl/controller-rpc";

import { type Result } from "@/components/search";
import SearchResultRow from "@/components/search/SearchResultRow.vue";

export default defineComponent({
  name: "SearchResultsList",
  components: {
    SearchResultRow,
  },
  props: {
    results: {
      type: Object as PropType<Result[]>,
      required: true,
    },
    showHighlightedContent: {
      type: Boolean,
      default: true,
    },
    contextWorkspaceId: {
      type: String as PropType<WorkspaceId | null>,
      default: null,
    },
    showGlobalContext: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["choose"],
  setup(props, { emit }) {
    const { results } = toRefs(props);
    const selected = ref(0);
    watch(results, () => {
      selected.value = 0;
    });
    const resultEls = ref<HTMLElement[]>([]);

    const clamp = (n: number, min: number, max: number) =>
      Math.min(Math.max(n, min), max);

    const selectResult = (dir: -1 | 1) => {
      selected.value = clamp(selected.value + dir, 0, results.value.length - 1);
    };

    const scrollSelectedResultIntoView = () => {
      const selectedResult = resultEls.value[selected.value];
      nextTick(() => {
        selectedResult.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    };

    const navigatePreviousResult = () => {
      selectResult(-1);
      scrollSelectedResultIntoView();
    };

    const navigateNextResult = () => {
      selectResult(1);
      scrollSelectedResultIntoView();
    };

    /**
     * Returns true when the keys are handled, and false otherwise
     */
    const onKeyDown = (event: KeyboardEvent): boolean => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          event.stopPropagation();
          navigatePreviousResult();
          break;
        case "ArrowDown":
          event.preventDefault();
          event.stopPropagation();
          navigateNextResult();
          break;
        case "Enter":
          event.preventDefault();
          event.stopPropagation();
          emit("choose", selected.value);
          break;
        default:
          return false;
      }
      return true;
    };

    const onMouseOver = (index: number) => {
      selected.value = index;
    };

    const resultKey = (result: Result, index: number) => {
      const location = result.searchResult.id.location;
      const locationKey = (() => {
        if (!location) return "title";
        if (location.location === "notes") return `notes-${location.block}`;
        if (location.location === "other")
          return `other-${location.location_type}`;
        return location.location;
      })();
      return `result-${index}-${result.id.kind}-${result.id.id}-${locationKey}`;
    };

    return {
      selected,
      resultEls,
      onKeyDown,
      onMouseOver,
      resultKey,
    };
  },
});
</script>

<style lang="scss" scoped>
.result {
  @include transition(background 100ms ease-out);
  &.selected {
    border-radius: $border-radius-m;

    @include themed() {
      background: t($grey-05);
    }
  }
}
</style>
